import { useEffect, useState } from 'react';
import { Icon, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useField } from 'formik';
import moment from 'moment';

import HelperText from './HelperText';

const DATE_FORMAT = 'MM/DD/YYYY';

export const tryParseDate = value => {
	let possibleDate = null;

	if (!value) {
		return null;
	}

	if (!/\/|-/.test(value) && value.length == 4) {
		value = `${value.substring(0, 2)}/${value.substring(2, 4)}/${moment().year()}`;

		possibleDate = moment(value, ['MM/DD/YYYY', 'MM/DD/YY'], true);

		if (possibleDate.isValid()) {
			return possibleDate;
		}
	} else if (!/\/|-/.test(value) && (value.length == 6 || value.length == 8)) {
		value = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4)}`;

		possibleDate = moment(value, ['MM/DD/YYYY', 'MM/DD/YY'], true);

		if (possibleDate.isValid()) {
			return possibleDate;
		}
	} else {
		possibleDate = moment(value, ['M/D/YY', 'MM/D/YY', 'M/DD/YY', 'MM/D/YYYY', 'M/DD/YYYY', 'M/D/YYYY', 'MM/DD/YYYY', 'MM/DD/YY'], true);

		if (possibleDate.isValid()) {
			return possibleDate;
		} else {
			return null;
		}
	}
};

export default function DatePickerField({ name, defaultValue, onChange, label, margin = 'normal', required, maxLength, predicted = undefined, helperText, disabled }) {
	const [field, meta, helper] = useField(name);

	useEffect(() => {
		if (defaultValue) {
			helper.setValue(moment(defaultValue));
		}
	}, []);

	const handleBlur = event => {
		const possibleDate = tryParseDate(event.target.value);

		if (!possibleDate) {
			helper.setValue(null);

			return;
		}

		helper.setValue(possibleDate.format(DATE_FORMAT));

		if (onChange) {
			onChange(possibleDate.format(DATE_FORMAT));
		}
	};

	const handlePaste = event => {
		event.stopPropagation();
		event.preventDefault();
		const dateToPaste = event.clipboardData.getData('text');

		const date = moment(dateToPaste, [
			'M/D/YYYY',
			'MM/DD/YYYY',
			'DD MMM YYYY',
			'DD MMMM YYYY',
			'YYYY-MM-DD',
			'YYYY-M-D',
			'YYYY.MM.DD',
			'YYYY.M.D',
			'DD.MM.YYYY',
			'D.M.YYYY',
			'YYYY/MM/DD',
			'YYYY-MM-DD'
		]).format(DATE_FORMAT);

		if (date !== 'Invalid date') {
			helper.setValue(moment(date));
		}
	};

	const handleChange = (date, isCalendarOpen) => {
		helper.setTouched(true);
		const possibleDate = tryParseDate(date);

		if (!possibleDate) {
			helper.setValue(null);

			return;
		}

		helper.setValue(possibleDate.format(DATE_FORMAT));

		if (onChange) {
			onChange(possibleDate.format(DATE_FORMAT), isCalendarOpen);
		}
	};

	const onKeyDown = event => {
		const keyOperations = {
			ArrowUp: date => date.add(1, 'days'),
			ArrowDown: date => date.subtract(1, 'days'),
			t: () => moment(),
			m: date => date.add(1, 'months'),
			l: date => date.subtract(1, 'months'),
			y: date => date.add(1, 'years'),
			p: date => date.subtract(1, 'years')
		};

		const operation = keyOperations[event.key];

		if (operation) {
			const date = moment(event.target.value, DATE_FORMAT);
			const value = operation(date);

			helper.setValue(value);

			if (onChange) {
				onChange(value, field);
			}
		}
	};

	return (
		<LocalizationProvider dateAdapter={AdapterMoment}>
			<DatePicker
				label={label}
				value={field.value ? moment(field.value) : null}
				onChange={handleChange}
				components={{
					OpenPickerIcon: () => <Icon>calendar_month</Icon>
				}}
				renderInput={params => (
					<TextField
						{...params}
						required={required}
						sx={{
							'& .MuiOutlinedInput-notchedOutline': {
								...(predicted && !meta.touched ? { borderColor: 'common.warning' } : {})
							}
						}}
						fullWidth
						margin={margin}
						error={!!meta.error}
						onKeyDown={onKeyDown}
						onBlur={handleBlur}
						onPaste={handlePaste}
						helperText={<HelperText meta={meta} required={required} maxLength={maxLength} helperText={helperText} />}
					/>
				)}
				disabled={disabled}
			/>
		</LocalizationProvider>
	);
}
